import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VendorHeader from "../../component/VendorHeader";
import VendorFooter from "../../component/VendorFooter";
import { API_BASE_URL, IMAGE_URL } from "../../utils/app_url";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../../component/Sidebar";
import { NotificationManager } from "react-notifications";

const AdminStores = () => {
    const { user } = useAuth();
    const { langJson } = useLanguage();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [updatingStoreId, setUpdatingStoreId] = useState(null);
    const [togglingStatusId, setTogglingStatusId] = useState(null);
    const [stores, setStores] = useState([]);

    const authHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.authorisation?.token}`,
    };

    const parseStoresList = responseData => {
        if (Array.isArray(responseData?.data)) return responseData.data;
        if (Array.isArray(responseData?.data?.data)) return responseData.data.data;
        if (Array.isArray(responseData?.stores)) return responseData.stores;
        if (Array.isArray(responseData?.data?.stores)) return responseData.data.stores;
        return [];
    };

    const fetchStores = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/stores`, {
                method: "GET",
                headers: authHeaders,
            });

            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                setStores(parseStoresList(responseData));
            } else {
                NotificationManager.warning(responseData?.msg || "Failed to fetch stores");
            }
        } catch (error) {
            NotificationManager.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const getFeaturedValue = item =>
        Number(item?.is_featured ?? item?.featured ?? item?.isFeatured ?? 0) === 1;

    const updateStoreFeaturedState = (storeId, nextValue) => {
        setStores(prev =>
            prev.map(store =>
                Number(store?.id) === Number(storeId)
                    ? {
                        ...store,
                        is_featured: nextValue ? 1 : 0,
                        featured: nextValue ? 1 : 0,
                    }
                    : store
            )
        );
    };

    const attemptToggleFeatured = async (storeId, nextValue) => {
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/stores/${storeId}/featured`, {
                method: "PATCH",
                headers: authHeaders,
                body: JSON.stringify({ is_featured: nextValue ? 1 : 0 }),
            });
            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                return responseData;
            }
            throw new Error(responseData?.msg || "Failed to update store");
        } catch (error) {
            throw error;
        }
    };

    const onToggleFeatured = async item => {
        if (!item?.id || updatingStoreId) return;
        const storeId = item.id;
        const currentValue = getFeaturedValue(item);
        const nextValue = !currentValue;

        setUpdatingStoreId(storeId);
        updateStoreFeaturedState(storeId, nextValue);

        try {
            const responseData = await attemptToggleFeatured(storeId, nextValue);
            NotificationManager.success(responseData?.msg || "Store updated");
        } catch (error) {
            updateStoreFeaturedState(storeId, currentValue);
            NotificationManager.warning(error?.message || "Failed to update store");
        } finally {
            setUpdatingStoreId(null);
        }
    };

    const toggleVendorStatus = async (storeId, currentStatus) => {
        if (togglingStatusId) return;
        const newStatus = currentStatus === 1 ? 0 : 1;

        setTogglingStatusId(storeId);
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/stores/${storeId}/toggle-status`, {
                method: "PATCH",
                headers: authHeaders,
                body: JSON.stringify({ is_active: newStatus }),
            });
            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                setStores(prev =>
                    prev.map(store =>
                        Number(store?.id) === Number(storeId)
                            ? { ...store, is_active: newStatus }
                            : store
                    )
                );
                NotificationManager.success(responseData?.msg || "Vendor status updated");
            } else {
                NotificationManager.warning(responseData?.msg || "Failed to update vendor status");
            }
        } catch (error) {
            NotificationManager.error("Something went wrong");
        } finally {
            setTogglingStatusId(null);
        }
    };

    useEffect(() => {
        fetchStores();
    }, []);

    return (
        <div>
            <div className="dashHeader">
                <VendorHeader />
            </div>
            <div className="adminDash">
                <Sidebar />
                <div className="rightSide rightTop">
                    <h2>{langJson?.stores || "Stores"}</h2>

                    <div className="prdtTable">
                        <table>
                            <thead>
                                <tr>
                                    <th>Logo</th>
                                    <th>{langJson?.name || "Name"}</th>
                                    <th>{langJson?.email || "Email"}</th>
                                    <th>{langJson?.phone || "Phone"}</th>
                                    <th>Plan</th>
                                    <th>Plan Start Date</th>
                                    <th>Amount</th>
                                    <th>Payment Method</th>
                                    <th>IBAN</th>
                                    <th>{langJson?.featured || "Featured"}</th>
                                    <th>Status</th>
                                    <th>{langJson?.action || "Action"}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && stores.length === 0 && (
                                    <tr>
                                        <td colSpan="12">{langJson?.noitemsfound || "No stores found"}</td>
                                    </tr>
                                )}

                                {loading && (
                                    <tr>
                                        <td colSpan="12">{langJson?.pleasewait || "Loading..."}</td>
                                    </tr>
                                )}

                                {!loading &&
                                    stores.map(item => (
                                        <tr key={item?.id}>
                                            <td className="stores-clickable-cell" onClick={() => navigate(`/admin/stores/${item?.id}`)}>
                                                <div className="stores-table-logo">
                                                    {item?.logo ? (
                                                        <img
                                                            src={IMAGE_URL + item.logo}
                                                            alt={item?.store_name}
                                                        />
                                                    ) : (
                                                        <span className="no-logo">No Logo</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="stores-clickable-cell stores-name-cell" onClick={() => navigate(`/admin/stores/${item?.id}`)}>{item?.store_name || item?.name || "-"}</td>
                                            <td>{item?.user?.email || item?.email || "-"}</td>
                                            <td>{item?.vendor_info?.phone || item?.phone || "-"}</td>
                                            <td>{item?.vendor_info?.plan_name || item?.active_plan?.name || "-"}</td>
                                            <td>{item?.vendor_info?.plan_start_date || "-"}</td>
                                            <td>{item?.vendor_info?.amount ? `EUR ${item?.vendor_info?.amount}` : "-"}</td>
                                            <td>{item?.vendor_info?.payment_method || "-"}</td>
                                            <td>{item?.vendor_info?.payment_method === "Direct Debit" && item?.vendor_info?.iban ? item?.vendor_info?.iban : "-"}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`featured-toggle-btn ${getFeaturedValue(item) ? 'featured' : 'not-featured'}`}
                                                    disabled={Number(updatingStoreId) === Number(item?.id)}
                                                    onClick={() => onToggleFeatured(item)}
                                                >
                                                    {getFeaturedValue(item) ? "Yes" : "No"}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`featured-toggle-btn ${item?.is_active === 1 ? 'featured' : 'not-featured'}`}
                                                    disabled={Number(togglingStatusId) === Number(item?.id)}
                                                    onClick={() => toggleVendorStatus(item?.id, item?.is_active)}
                                                >
                                                    {item?.is_active === 1 ? "Active" : "Inactive"}
                                                </button>
                                            </td>
                                            <td className="actionHolder">
                                                <div className="actionDiv">
                                                    <button onClick={() => navigate(`/admin/stores/${item?.id}`)}>
                                                        {langJson?.view || "View"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <VendorFooter />
        </div>
    );
};

export default AdminStores;